import { Dispatch, SetStateAction } from 'react';

import {
  PrimaryButton,
  TertiaryButton,
} from '@changegps/horizon-component-library';
import { Box, Skeleton, Stack, Typography, useTheme } from '@mui/material';

import { Product } from '../types';

interface ProductTabsProps {
  data?: Product[];
  loading: boolean;
  toggleDrawer: () => void;
  toggleConfirmationDialog: () => void;
  setActiveTab: Dispatch<SetStateAction<number>>;
  onSelectUnsubscribe: Dispatch<SetStateAction<number>>;
}

export const ProductTabs = ({
  data,
  loading,
  toggleDrawer,
  toggleConfirmationDialog,
  setActiveTab,
  onSelectUnsubscribe,
}: ProductTabsProps) => {
  const theme = useTheme();

  if (loading)
    return (
      <>
        {Array.from({ length: 5 }).map((_, idx1) => {
          return (
            <Stack
              key={idx1}
              width={200}
              borderTop={2}
              borderColor={theme.palette.primary.main}
              pt={1}
            >
              <Skeleton height={60} width={'60%'} />
              {Array.from({ length: 4 }).map((_, idx2) => (
                <Box key={idx2}>
                  <Skeleton height={36} width={'40%'} />
                  <Skeleton height={36} width={'70%'} />
                </Box>
              ))}
            </Stack>
          );
        })}
      </>
    );

  if (Array.isArray(data) && data.length > 0)
    return (
      <>
        {data?.map((item, index) => {
          return (
            <Stack
              key={index}
              width={200}
              borderTop={2}
              borderColor={theme.palette.primary.main}
              pt={1}
              spacing={2}
            >
              <Typography variant="h1" fontWeight={'700'}>
                {item.product_name}
              </Typography>
              <Box>
                <Typography>Class</Typography>
                <Typography
                  variant="h4"
                  fontWeight={'500'}
                  color={theme.palette.primary.main}
                >
                  {item.tier_name}
                </Typography>
              </Box>
              <Box>
                <Typography>Available licences</Typography>
                <Typography variant="h4" fontWeight={'500'}>
                  {item.remaining_licence_count}/{item.licence_count}
                </Typography>
              </Box>
              <Box>
                <Typography>Licences in use</Typography>
                <Typography variant="h4" fontWeight={'500'}>
                  {item.used_licence_count}
                </Typography>
              </Box>
              <Box>
                <Typography>Cost</Typography>
                <Typography variant="h4" fontWeight={'500'}>
                  ${item.dollar_amount}
                </Typography>
              </Box>
              <Stack spacing={1}>
                {item.product_id !== 6 && (
                  <PrimaryButton
                    disabled={item.is_locked_in}
                    onClick={() => {
                      setActiveTab(index);
                      toggleDrawer();
                    }}
                  >
                    Modify
                  </PrimaryButton>
                )}
                <TertiaryButton
                  onClick={() => {
                    onSelectUnsubscribe(item.product_id as number);
                    toggleConfirmationDialog();
                  }}
                >
                  Unsubscribe
                </TertiaryButton>
              </Stack>
              <Box sx={{ pt: 1 }}>
                {item.future_changes.length > 0 && (
                  <Typography variant="subtitle2">
                    Next Lock-in Cycle Changes:
                  </Typography>
                )}
                <ul style={{ paddingInlineStart: 28 }}>
                  {item.future_changes.map((text: string, index: number) => (
                    <li key={index}>
                      <Typography variant="subtitle2">{text}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            </Stack>
          );
        })}
      </>
    );

  return <Typography>Error loading subscriptions info</Typography>;
};
